import { lazy, useEffect, useRef, useState } from 'react';
const Logo = lazy(() => import('@assets/images/logo/logo-no-bg.svg'));
const Menu = lazy(() => import('@assets/images/navbar/menu.svg'));
const Close = lazy(() => import('@assets/images/navbar/close.svg'));
const Home = lazy(() => import('@assets/images/navbar/home.svg'));
const Qrcode = lazy(() => import('@assets/images/navbar/qrcode.svg'));
const PastTransactions = lazy(() => import('@assets/images/navbar/past-transactions.svg'));
const DealsPrivileges = lazy(() => import('@assets/images/navbar/deals-privileges.svg'));
const ContactUs = lazy(() => import('@assets/images/navbar/contact-us.svg'));
const Referral = lazy(() => import('@assets/images/navbar/referral.svg'));
const NotificationsDesktopOn = lazy(() => import('@assets/images/navbar/noti-on-desktop.svg'));
const NotificationDesktopOff = lazy(() => import('@assets/images/navbar/noti-off-desktop.svg'));
const NotificationOn = lazy(() => import('@assets/images/navbar/notification-on.svg'));
const NotificationOff = lazy(() => import('@assets/images/navbar/notification-off.svg'));
const ResourceGuide = lazy(() => import('@assets/images/navbar/resource-guide.svg'));
const Merchant = lazy(() => import('@assets/images/navbar/merchant.svg'));
const Tnc = lazy(() => import('@assets/images/navbar/tnc.svg'));
const Logout = lazy(() => import('@assets/images/navbar/logout.svg'));

import { Link } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { LocalStorageKey } from '@constants/storage-keys.constants';
import { AppState } from '@/state';
import useBodyScrollLock from '@hooks/useBodyScrollLock';
import { ROUTES } from '@constants/route.constant';
import { handleLogout } from '@helpers/auth.helper';
import ArrowSVG, { Rotate } from '../ArrowSVG';
import { useSelector } from 'react-redux';

type NavbarProps = {
	enableNotification: () => void;
	isNotificationOn: boolean;
};

export default function Navbar({ enableNotification, isNotificationOn }: NavbarProps) {
	const links = [
		{ id: 1, name: 'Home', link: '/', icon: <Home /> },
		{ id: 2, name: 'QR Code', link: '/qr-code', icon: <Qrcode /> },
		{ id: 3, name: 'Past Transactions', link: `/${ROUTES.TRANSACTIONS}`, icon: <PastTransactions /> },
		{ id: 4, name: 'Deals & Privileges', link: `/${ROUTES.DEALS_PRIVILEGES}`, icon: <DealsPrivileges /> },
		{ id: 5, name: 'Contact Us', link: '/contact-us', icon: <ContactUs /> },
		{ id: 6, name: 'Referral', link: '/referral', icon: <Referral /> },
	];

	const { MP_URL } = JSON.parse(localStorage.getItem(LocalStorageKey.CONFIG_KEY) || '{}');
	const quickLinks = [
		{
			id: 1,
			name: 'Resource Guide',
			link: 'https://www.singaporeair.com/en_UK/sg/ppsclub-krisflyer/use-miles/krisplus/krisplus-merchant/',
			icon: <ResourceGuide />,
		},
		{ id: 2, name: 'Merchant Admin Portal', link: MP_URL, icon: <Merchant /> },
		{
			id: 3,
			name: 'Terms of Use',
			link: 'https://www.singaporeair.com/content/dam/sia/web-assets/pdfs/ppsclub-krisflyer/use-miles/krisplus/KrisMerchantAppPortalTermsofUse.pdf',
			icon: <Tnc />,
		},
	];
	const { APP_DATA } = JSON.parse(localStorage.getItem(LocalStorageKey.CONFIG_KEY) || '{}');

	const [isNavOpen, setIsNavOpen] = useState(false);
	const [isQuickLinksDesktopOpen, setIsQuickLinksDesktopOpen] = useState(false);
	const [isQuickLinksMobileOpen, setIsQuickLinksMobileOpen] = useState(true);
	const { _id: uid } = useSelector((state: AppState) => state.userState.user);

	const quickLinksRef = useRef<HTMLDivElement | null>(null);
	const { instance } = useMsal();

	const notificationButtonHandler = () => {
		setIsNavOpen(false);
		enableNotification();
	};

	useEffect(() => {
		const handler = (e: Event) => {
			if (!quickLinksRef?.current?.contains(e.target as Node)) {
				setIsQuickLinksDesktopOpen(false);
			}
		};
		document.addEventListener('mousedown', handler);
		return () => {
			document.removeEventListener('mousedown', handler);
		};
	}, [isNavOpen]);

	useBodyScrollLock(isNavOpen);

	return (
		<div className={`shadow w-full bg-white font-semibold ${isNavOpen ? 'z-40' : 'z-20'}`}>
			<div className='py-4 px-6 desktop:flex justify-between items-center desktop:container z-30'>
				<div className='flex justify-between items-center'>
					{/* Logo */}
					<div className='flex cursor-pointer items-center'>
						<Link to='/'>
							<div className='w-[72px] desktop:w-[80px] desktop:h-[36px]'>
								<Logo />
							</div>
						</Link>
					</div>

					{isNavOpen ? (
						<>
							{/* Close button */}
							<div onClick={() => setIsNavOpen(false)} className='cursor-pointer desktop:hidden'>
								<Close />
							</div>
						</>
					) : (
						<>
							{/* Hamburger */}
							<div onClick={() => setIsNavOpen(true)} className='cursor-pointer desktop:hidden'>
								<Menu />
							</div>
						</>
					)}
				</div>

				{/* Desktop */}
				{/* Navigation Items */}
				<ul className={'hidden desktop:flex flex-row items-center gap-4 list-none'}>
					{links.slice(1).map((link, index) => (
						<Link to={link.link} key={index}>
							<li key={index} className='font-semibold desktop:p-2 hover:text-blue-grey'>
								{link.name}
							</li>
						</Link>
					))}

					{/* Quicklinks Items */}
					<div ref={quickLinksRef}>
						<li
							className={`flex items-center hover:text-blue-grey p-2 gap-[2px] cursor-pointer ${
								isQuickLinksDesktopOpen && 'bg-cyan-lightest rounded-lg'
							}`}
							onClick={() => setIsQuickLinksDesktopOpen(!isQuickLinksDesktopOpen)}>
							<span>Quicklinks</span>
							<ArrowSVG rotate={isQuickLinksDesktopOpen ? Rotate.TOP : Rotate.BOTTOM} width={16} height={16} animation={true} />
						</li>

						<li>
							<div className='relative'>
								{isQuickLinksDesktopOpen && (
									<ul className='absolute top-[8px] right-0 bg-grey-lightest rounded-lg p-2 flex flex-col gap-4 z-50 list-none'>
										{quickLinks.map((item) => (
											<Link to={item.link} key={item.id} target='_blank'>
												<li
													key={item.id}
													className='cursor-pointer gap-1 flex items-center w-[152px]'
													onClick={() => setIsNavOpen(false)}>
													<div className='w-8 h-8 flex-none'>{item.icon}</div>
													<div className='grow'>{item.name}</div>
												</li>
											</Link>
										))}
									</ul>
								)}
							</div>
						</li>
					</div>

					<li className='font-semibold desktop:p-2 flex items-center cursor-pointer'>
						{isNotificationOn ? (
							<>
								<NotificationsDesktopOn />
								<div className='w-[42px] h-[18px] rounded-2xl bg-white-dark flex items-center justify-center ml-1 pt-[2px]'>
									<span className='w-1 h-1 bg-green-dark rounded-full mr-1'></span>
									<span className='text-sm font-semibold text-green-dark'>ON</span>
								</div>
							</>
						) : (
							<>
								<NotificationDesktopOff />
								<span className='underline underline-offset-2 text-base text-cyan ml-1' onClick={enableNotification}>
									Turn on
								</span>
							</>
						)}
					</li>
				</ul>

				{/*  Logout */}
				<div className='hidden desktop:flex'>
					<button onClick={() => handleLogout(uid, instance)} className='font-semibold p-2 bg-cyan rounded-lg text-white w-fit'>
						Log out
					</button>
				</div>

				{/* Mobile & Tablet */}

				<div className='desktop:hidden'>
					<div
						className={
							'fixed z-50 w-[calc(100%-209px)] h-[calc(100%-64px)] top-[64px] left-0 bg-blue-grey-darkest transition-all ease-in-out duration-[500ms] ' +
							(isNavOpen ? 'opacity-80 visible' : 'opacity-0 invisible')
						}
						onClick={() => setIsNavOpen(false)}></div>
					<ul
						className={
							'navbarScroll fixed top-[64px] right-0 w-[209px] h-[calc(100%-64px)] transition-all ease-in-out duration-[500ms] bg-grey-lightest z-50 overflow-y-scroll justify-between flex flex-col list-none' +
							(isNavOpen ? 'opacity-100 visible' : 'opacity-0 invisible')
						}>
						<div>
							{/* Navigation Items */}
							{links.map((item) => (
								<Link to={item.link} key={item.id}>
									<li key={item.id} className='px-4 py-1 cursor-pointer gap-1 flex items-center' onClick={() => setIsNavOpen(false)}>
										<>{item.icon}</>
										<span className='p-2'>{item.name}</span>
									</li>
								</Link>
							))}

							<li className='px-4 py-1 flex items-center cursor-pointer'>
								<div className='w-10 h-10'>{isNotificationOn ? <NotificationOn /> : <NotificationOff />}</div>
								<div className='p-2 w-full flex flex-col'>
									<span>Notifications</span>
									{isNotificationOn ? (
										<div className='w-[42px] h-4 rounded-2xl bg-white-dark flex items-center justify-center'>
											<span className='w-1 h-1 bg-green-dark rounded-full mr-1'></span>
											<span className='text-sm font-semibold text-green-dark'>ON</span>
										</div>
									) : (
										<span className='underline underline-offset-2 text-sm text-cyan' onClick={notificationButtonHandler}>
											Turn on
										</span>
									)}
								</div>
							</li>
							{/* Quicklinks Items */}
							<li className='px-4 py-1 list-none cursor-pointer' onClick={() => setIsQuickLinksMobileOpen(!isQuickLinksMobileOpen)}>
								<div className='p-2 flex gap-[51px] items-center'>
									<span>Quicklinks</span>
									<ArrowSVG rotate={isQuickLinksMobileOpen ? Rotate.TOP : Rotate.BOTTOM} animation={true} />
								</div>
							</li>
							{isQuickLinksMobileOpen &&
								quickLinks.map((item) => (
									<Link to={item.link} key={item.id} target='_blank'>
										<li key={item.id} className='px-4 py-1 cursor-pointer gap-1 flex items-center' onClick={() => setIsNavOpen(false)}>
											<div className='w-10 h-10 flex-none'>{item.icon}</div>
											<span className='p-2 grow'>{item.name}</span>
										</li>
									</Link>
								))}
						</div>
						<div className='w-[209px]'>
							<hr className='border-grey-light h-[1px]' />
							<li className='px-4 py-1 cursor-pointer gap-1 flex' onClick={() => handleLogout(uid, instance)}>
								<Logout />
								<span className='p-2'>Log out</span>
							</li>
							<li className='flex text-xs font-normal px-6 py-3 justify-end list-none'>
								Version {APP_DATA?.app_version} build {APP_DATA?.app_build}
							</li>
						</div>
					</ul>
				</div>
			</div>
		</div>
	);
}
